import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StringListEditor from "./StringListEditor";

const BASE_PATH = "/api/admin/treatments";
const SECTION_COUNT = 5;

type TreatmentRow = {
  id: number;
  slug: string;
  title: string;
  image: string;
  list: string[] | null;
  details: Record<string, any> | null;
};

type TreatmentForm = {
  slug: string;
  title: string;
  image: string;
  list: string[];
  headings: string[];
  sections: string[];
  listInfo: string[];
  listSleep: string[];
  linkSleep: string;
};

function emptyForm(): TreatmentForm {
  return {
    slug: "",
    title: "",
    image: "",
    list: [],
    headings: Array(SECTION_COUNT).fill(""),
    sections: Array(SECTION_COUNT).fill(""),
    listInfo: [],
    listSleep: [],
    linkSleep: "",
  };
}

function rowToForm(row: TreatmentRow): TreatmentForm {
  const details = row.details ?? {};
  return {
    slug: row.slug,
    title: row.title,
    image: row.image,
    list: row.list ?? [],
    headings: Array.from(
      { length: SECTION_COUNT },
      (_, i) => details[`heading${i + 1}`] ?? ""
    ),
    sections: Array.from(
      { length: SECTION_COUNT },
      (_, i) => details[`section${i + 1}`] ?? ""
    ),
    listInfo: details.listInfo ?? [],
    listSleep: details.listSleep ?? [],
    linkSleep: details.linkSleep ?? "",
  };
}

function formToPayload(form: TreatmentForm) {
  const details: Record<string, unknown> = {};
  form.headings.forEach((heading, i) => {
    const n = i + 1;
    const trimmedHeading = heading.trim();
    const trimmedBody = form.sections[i].trim();
    if (trimmedHeading) details[`heading${n}`] = trimmedHeading;
    if (trimmedBody) details[`section${n}`] = trimmedBody;
  });
  const listInfo = form.listInfo.map((s) => s.trim()).filter(Boolean);
  const listSleep = form.listSleep.map((s) => s.trim()).filter(Boolean);
  if (listInfo.length) details.listInfo = listInfo;
  if (listSleep.length) details.listSleep = listSleep;
  if (form.linkSleep.trim()) details.linkSleep = form.linkSleep.trim();

  return {
    slug: form.slug.trim(),
    title: form.title.trim(),
    image: form.image.trim(),
    list: form.list.map((s) => s.trim()).filter(Boolean),
    details: Object.keys(details).length ? details : null,
  };
}

export default function TreatmentsAdmin() {
  const [rows, setRows] = useState<TreatmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TreatmentForm>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setLoadError(null);
    fetch(BASE_PATH, { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            res.status === 401
              ? "Du er ikke lenger innlogget."
              : "Kunne ikke laste behandlinger."
          );
        }
        return res.json();
      })
      .then(setRows)
      .catch((err) =>
        setLoadError(
          err instanceof Error ? err.message : "Kunne ikke laste behandlinger."
        )
      )
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: TreatmentRow) => {
    setEditingId(row.id);
    setForm(rowToForm(row));
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSubmit = async () => {
    const payload = formToPayload(form);
    if (!payload.slug || !payload.title || !payload.image) {
      setFormError("Slug, tittel og bilde er påkrevd.");
      return;
    }

    const url = editingId ? `${BASE_PATH}/${editingId}` : BASE_PATH;
    const method = editingId ? "PUT" : "POST";
    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setFormError(data.error ?? "Lagring feilet.");
      return;
    }

    setDialogOpen(false);
    load();
  };

  const handleDelete = async (row: TreatmentRow) => {
    if (!window.confirm(`Slette "${row.title}"?`)) {
      return;
    }
    await fetch(`${BASE_PATH}/${row.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    load();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">Behandlinger</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
          Ny
        </Button>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Slug</TableCell>
              <TableCell>Tittel</TableCell>
              <TableCell>Bilde</TableCell>
              <TableCell align="right">Handlinger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.slug}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.image}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(row)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row)} size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Rediger behandling" : "Ny behandling"}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          {formError && <Alert severity="error">{formError}</Alert>}

          <TextField
            label="Slug (brukes i URL-en)"
            value={form.slug}
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
            required
            fullWidth
          />
          <TextField
            label="Tittel"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
            fullWidth
          />
          <TextField
            label="Bilde (sti under public/, f.eks. /ear.webp)"
            value={form.image}
            onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
            required
            fullWidth
          />

          <StringListEditor
            label="Punktliste (vises rett under bildet)"
            items={form.list}
            onChange={(list) => setForm((f) => ({ ...f, list }))}
          />

          <Divider />
          <Typography variant="subtitle2">Avsnitt (valgfritt – opptil 5)</Typography>
          {form.headings.map((heading, i) => (
            <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label={`Overskrift ${i + 1} (valgfri)`}
                value={heading}
                onChange={(e) => {
                  const headings = [...form.headings];
                  headings[i] = e.target.value;
                  setForm((f) => ({ ...f, headings }));
                }}
                size="small"
                fullWidth
              />
              <TextField
                label={`Tekst ${i + 1}`}
                value={form.sections[i]}
                onChange={(e) => {
                  const sections = [...form.sections];
                  sections[i] = e.target.value;
                  setForm((f) => ({ ...f, sections }));
                }}
                multiline
                minRows={2}
                fullWidth
              />
            </Box>
          ))}

          <Divider />
          <StringListEditor
            label="Punktliste i avsnitt 2"
            helperText="Vises rett under overskrift 2 – brukes bl.a. for testene under Allergi."
            items={form.listInfo}
            onChange={(listInfo) => setForm((f) => ({ ...f, listInfo }))}
          />
          <StringListEditor
            label="Utstyrsliste"
            helperText="Vises etter avsnittene – brukes for Søvn-behandlingen."
            items={form.listSleep}
            onChange={(listSleep) => setForm((f) => ({ ...f, listSleep }))}
          />
          <TextField
            label="Lenke til informasjonsvideo (URL)"
            value={form.linkSleep}
            onChange={(e) => setForm((f) => ({ ...f, linkSleep: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Avbryt</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Lagre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
